import {
  BuildRecoveryError,
  isAssetLoadError,
  recoverFromAssetLoadError,
} from '@/utils/buildRecovery';
import type {
  LazyRouteFunction,
  NonIndexRouteObject,
  IndexRouteObject,
} from 'react-router';

export const LAST_AUTHORIZED_PATH_KEY = 'atlas_last_authorized_path';

/**
 * Returns the last successfully-rendered authorized admin path stored in sessionStorage,
 * or `undefined` if no prior path is stored.
 */
export function getLastAuthorizedPath(): string | undefined {
  try {
    return sessionStorage.getItem(LAST_AUTHORIZED_PATH_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Stores the given path as the last authorized admin path in sessionStorage.
 */
export function setLastAuthorizedPath(path: string): void {
  try {
    sessionStorage.setItem(LAST_AUTHORIZED_PATH_KEY, path);
  } catch {
    // sessionStorage unavailable — silently ignore
  }
}

/**
 * Default authenticated landing path for users with support access.
 * Reuses the same value that `getDefaultRedirectUri(true, roles)` returns
 * for a non-report-only user with support access.
 */
export const DEFAULT_LANDING_PATH = '/support';

export function importOrReload<
  LazyRouteFunctionType extends NonIndexRouteObject | IndexRouteObject =
    NonIndexRouteObject,
>(dynamicImportFn: () => Promise<unknown>) {
  return (async () => {
    try {
      return await dynamicImportFn();
    } catch (error) {
      if (!isAssetLoadError(error)) {
        throw error;
      }

      const result = await recoverFromAssetLoadError(error);
      if (result === 'reloading') {
        return new Promise<never>(() => undefined);
      }

      throw new BuildRecoveryError();
    }
  }) as LazyRouteFunction<LazyRouteFunctionType>;
}
